/**
 * ClimatePlans shared choropleth palettes and bin utilities.
 * Rule: darker = more. No red-green sequential ramps for quantitative climate maps.
 */

export type ChoroplethSemanticFamily = 'neutral' | 'benefit' | 'burden' | 'diverging' | 'political';

/** Shared no-data fill — neutral gray, subordinate to tinted data classes. */
export const CHOROPLETH_NO_DATA_FILL = '#C6CBD2';

/**
 * Lightest valid class is intentionally tinted (not near-white) for OSM basemap legibility.
 * 5-class subsets align with design targets at indices 0,2,4,5,7 via pickSubsetEvenly.
 */
export const NEUTRAL_BLUE_8 = [
  '#D9EAF5',
  '#C4DFF0',
  '#A9CFE5',
  '#8BBFDD',
  '#6BAED6',
  '#3182BD',
  '#1A6BA8',
  '#08519C',
] as const;

export const BENEFIT_TEAL_8 = [
  '#D7ECE7',
  '#BFE4DA',
  '#A8D8CC',
  '#8ACFB8',
  '#66C2A4',
  '#2A9D78',
  '#138566',
  '#006D54',
] as const;

export const BURDEN_ORANGE_8 = [
  '#FBE1C5',
  '#FDD0A8',
  '#FDBB84',
  '#FDAA6E',
  '#FC8D59',
  '#E6550D',
  '#C54708',
  '#A63603',
] as const;

/** Default Leaflet tract polygon strokes (non-hovered). */
export const CHOROPLETH_TRACT_STROKE_COLOR = 'rgba(255, 255, 255, 0.45)';
export const CHOROPLETH_TRACT_STROKE_WEIGHT = 0.55;
export const CHOROPLETH_TRACT_FILL_OPACITY = 0.82;
export const CHOROPLETH_TRACT_NODATA_FILL_OPACITY = 0.72;
/** OSM basemap tile opacity when thematic layer should dominate. */
export const CHOROPLETH_BASEMAP_OPACITY = 0.7;
export const CHOROPLETH_TRACT_HOVER_STROKE_COLOR = '#1e293b';
export const CHOROPLETH_TRACT_HOVER_STROKE_WEIGHT = 2.5;
export const CHOROPLETH_TRACT_HOVER_FILL_OPACITY = 0.92;

export type ChoroplethPathStyle = {
  fillColor: string;
  weight: number;
  opacity: number;
  color: string;
  fillOpacity: number;
};

export function choroplethDataPathStyle(fillColor: string): ChoroplethPathStyle {
  return {
    fillColor,
    weight: CHOROPLETH_TRACT_STROKE_WEIGHT,
    opacity: 1,
    color: CHOROPLETH_TRACT_STROKE_COLOR,
    fillOpacity: CHOROPLETH_TRACT_FILL_OPACITY,
  };
}

export function choroplethNoDataPathStyle(): ChoroplethPathStyle {
  return {
    fillColor: CHOROPLETH_NO_DATA_FILL,
    weight: CHOROPLETH_TRACT_STROKE_WEIGHT,
    opacity: 1,
    color: CHOROPLETH_TRACT_STROKE_COLOR,
    fillOpacity: CHOROPLETH_TRACT_NODATA_FILL_OPACITY,
  };
}

export function choroplethHoverPathStyle(base: ChoroplethPathStyle): ChoroplethPathStyle {
  return {
    ...base,
    weight: CHOROPLETH_TRACT_HOVER_STROKE_WEIGHT,
    color: CHOROPLETH_TRACT_HOVER_STROKE_COLOR,
    fillOpacity: CHOROPLETH_TRACT_HOVER_FILL_OPACITY,
  };
}

/** Political diverging: R → neutral center → D (conventional, not climate semantic). */
export const POLITICAL_DIVERGING_7 = [
  '#8B0000',
  '#DC143C',
  '#FFA07A',
  '#F5F5F5',
  '#87CEEB',
  '#4169E1',
  '#000080',
] as const;

const FAMILY_SOURCE: Record<Exclude<ChoroplethSemanticFamily, 'diverging'>, readonly string[]> = {
  neutral: NEUTRAL_BLUE_8,
  benefit: BENEFIT_TEAL_8,
  burden: BURDEN_ORANGE_8,
  political: POLITICAL_DIVERGING_7,
};

/**
 * Pick `classCount` colors from an 8-step family (or 7 for political), light → dark.
 */
export function getChoroplethPalette(family: ChoroplethSemanticFamily, classCount: number): string[] {
  const n = Math.max(2, Math.min(8, Math.round(classCount)));
  if (family === 'political') {
    const src = [...POLITICAL_DIVERGING_7];
    if (n === src.length) return src;
    return pickSubsetEvenly(src, n);
  }
  if (family === 'diverging') {
    // Blue ↔ orange around zero (not used for political lean)
    const blue = pickSubsetEvenly([...NEUTRAL_BLUE_8].reverse(), Math.ceil(n / 2));
    const orange = pickSubsetEvenly([...BURDEN_ORANGE_8], Math.floor(n / 2));
    return [...blue.slice(0, Math.ceil(n / 2)).reverse(), ...orange.slice(-Math.floor(n / 2))];
  }
  const src = FAMILY_SOURCE[family];
  return pickSubsetEvenly([...src], n);
}

function pickSubsetEvenly(source: string[], count: number): string[] {
  if (count >= source.length) return source;
  if (count <= 1) return [source[source.length - 1]];
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 1 : i / (count - 1);
    const idx = Math.round(t * (source.length - 1));
    out.push(source[idx]);
  }
  return out;
}

export type ChoroplethBin = {
  lower: number;
  upper: number;
  color: string;
  label: string;
  /** inclusive lower, inclusive upper unless noted */
};

export type ValueFormatter = (value: number) => string;

const defaultFormatter: ValueFormatter = (v) =>
  Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });

/** Linear interpolated quantile on a sorted array (includes zeros). */
export function valueAtQuantile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (p <= 0) return sorted[0];
  if (p >= 1) return sorted[sorted.length - 1];
  const pos = (sorted.length - 1) * p;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (pos - lo) * (sorted[hi] - sorted[lo]);
}

function formatBinEdgeLabel(
  lower: number,
  upper: number,
  index: number,
  isLast: boolean,
  formatValue: ValueFormatter
): string {
  const same = Math.abs(upper - lower) < 1e-12;
  if (isLast) {
    if (index === 0 && same) return formatValue(lower);
    return `> ${formatValue(lower)}`;
  }
  if (index === 0) return `≤ ${formatValue(upper)}`;
  if (same) return formatValue(upper);
  return `${formatValue(lower)} – ${formatValue(upper)}`;
}

/**
 * Build `classCount` quantile bins from numeric values (includes zero).
 */
export function buildQuantileBins(
  values: number[],
  classCount: number,
  family: ChoroplethSemanticFamily,
  formatValue: ValueFormatter = defaultFormatter
): ChoroplethBin[] {
  const finite = values.filter((v) => v != null && Number.isFinite(v)).sort((a, b) => a - b);
  const palette = getChoroplethPalette(family, classCount);
  if (finite.length === 0) {
    return palette.map((color, i) => ({
      lower: i,
      upper: i,
      color,
      label: '—',
    }));
  }

  const edges: number[] = [];
  for (let i = 0; i <= classCount; i++) {
    edges.push(valueAtQuantile(finite, i / classCount));
  }
  for (let i = 1; i < edges.length; i++) {
    if (edges[i] < edges[i - 1]) edges[i] = edges[i - 1];
  }

  const bins: ChoroplethBin[] = [];
  for (let i = 0; i < classCount; i++) {
    const lower = edges[i];
    const upper = edges[i + 1];
    const isLast = i === classCount - 1;
    const label = formatBinEdgeLabel(lower, upper, i, isLast, formatValue);
    bins.push({ lower, upper, color: palette[i], label });
  }
  return bins;
}

/**
 * When many tracts are exactly zero, reserve the lowest class for 0 and quantile the rest.
 */
export function buildZeroAwareQuantileBins(
  values: number[],
  classCount: number,
  family: ChoroplethSemanticFamily,
  formatValue: ValueFormatter = defaultFormatter,
  zeroShareThreshold = 0.08
): ChoroplethBin[] {
  const finite = values.filter((v) => v != null && Number.isFinite(v)).sort((a, b) => a - b);
  if (finite.length === 0) return buildQuantileBins(values, classCount, family, formatValue);

  const zeros = finite.filter((v) => v === 0).length;
  const positive = finite.filter((v) => v > 0);
  if (positive.length === 0 || zeros / finite.length < zeroShareThreshold) {
    return buildQuantileBins(values, classCount, family, formatValue);
  }

  const palette = getChoroplethPalette(family, classCount);
  const positiveClasses = classCount - 1;
  const edges: number[] = [];
  for (let i = 0; i <= positiveClasses; i++) {
    edges.push(valueAtQuantile(positive, i / positiveClasses));
  }

  const bins: ChoroplethBin[] = [
    { lower: 0, upper: 0, color: palette[0], label: '0' },
  ];

  for (let i = 0; i < positiveClasses; i++) {
    const lower = edges[i];
    const upper = edges[i + 1];
    const isLast = i === positiveClasses - 1;
    const color = palette[i + 1];
    let label: string;
    if (isLast) {
      label = `> ${formatValue(lower)}`;
    } else if (i === 0) {
      label = `> 0 – ${formatValue(upper)}`;
    } else {
      label = formatBinEdgeLabel(lower, upper, i + 1, false, formatValue);
    }
    bins.push({ lower, upper, color, label });
  }
  return bins;
}

export function classifySequentialValueWithZeroClass(value: number, bins: ChoroplethBin[]): string {
  if (!Number.isFinite(value) || bins.length === 0) return CHOROPLETH_NO_DATA_FILL;
  if (value === 0 && bins[0]?.upper === 0 && bins[0]?.lower === 0) {
    return bins[0].color;
  }
  if (bins.length > 1 && bins[0].upper === 0 && value > 0) {
    return classifySequentialValue(value, bins.slice(1));
  }
  return classifySequentialValue(value, bins);
}

/** Classify value into bin (last bin includes values above previous upper bound). */
export function classifySequentialValue(value: number, bins: ChoroplethBin[]): string {
  if (!Number.isFinite(value) || bins.length === 0) return CHOROPLETH_NO_DATA_FILL;
  for (let i = 0; i < bins.length; i++) {
    const b = bins[i];
    const isLast = i === bins.length - 1;
    if (isLast) {
      if (value >= b.lower) return b.color;
      continue;
    }
    if (value <= b.upper) return b.color;
  }
  return bins[bins.length - 1].color;
}

/** Fixed percentile classes (walkability, UHI): bounds are upper limits per class. */
export function buildFixedPercentileBins(
  bounds: number[],
  family: 'benefit' | 'burden',
  labels: string[]
): ChoroplethBin[] {
  const palette = getChoroplethPalette(family, bounds.length);
  return bounds.map((upper, i) => ({
    lower: i === 0 ? 0 : bounds[i - 1],
    upper,
    color: palette[i],
    label: labels[i] ?? `${upper}`,
  }));
}

export function classifyFixedPercentile(value: number, bins: ChoroplethBin[]): string {
  if (!Number.isFinite(value)) return CHOROPLETH_NO_DATA_FILL;
  for (let i = 0; i < bins.length; i++) {
    if (value < bins[i].upper || (i === bins.length - 1 && value <= bins[i].upper)) {
      if (i === bins.length - 1) return bins[i].color;
      if (value <= bins[i].upper) return bins[i].color;
    }
    if (value <= bins[i].upper) return bins[i].color;
  }
  return bins[bins.length - 1].color;
}

/** UHI / walkability: [0,20), [20,40), ... last includes 100 */
export function classifyUpperBoundBins(value: number, uppers: number[], colors: string[]): string {
  if (!Number.isFinite(value)) return CHOROPLETH_NO_DATA_FILL;
  for (let i = 0; i < uppers.length; i++) {
    const prev = i === 0 ? -Infinity : uppers[i - 1];
    const upper = uppers[i];
    const isLast = i === uppers.length - 1;
    if (isLast) {
      if (value >= prev) return colors[i];
    } else if (value >= prev && value < upper) {
      return colors[i];
    }
  }
  return colors[colors.length - 1];
}

export function buildPoliticalDivergingBins(maxAbs: number, classCount = 7): ChoroplethBin[] {
  const M = maxAbs > 0 ? maxAbs : 1;
  const palette = getChoroplethPalette('political', classCount);
  const half = Math.floor(classCount / 2);
  const bins: ChoroplethBin[] = [];
  const edges: number[] = [];
  for (let i = 0; i <= classCount; i++) {
    const t = (i / classCount) * 2 - 1;
    edges.push(t * M);
  }
  for (let i = 0; i < classCount; i++) {
    const lower = edges[i];
    const upper = edges[i + 1];
    const fmt = (v: number) => {
      if (v > 0) return `D +${v.toFixed(1)}`;
      if (v < 0) return `R +${Math.abs(v).toFixed(1)}`;
      return 'Even';
    };
    bins.push({
      lower,
      upper,
      color: palette[i],
      label: `${fmt(lower)} – ${fmt(upper)}`,
    });
  }
  return bins;
}

export function classifyPoliticalValue(value: number, bins: ChoroplethBin[]): string {
  if (!Number.isFinite(value)) return CHOROPLETH_NO_DATA_FILL;
  for (let i = 0; i < bins.length; i++) {
    const { lower, upper } = bins[i];
    const isLast = i === bins.length - 1;
    if (isLast) {
      if (value >= lower) return bins[i].color;
    } else if (value >= lower && value < upper) {
      return bins[i].color;
    }
  }
  return bins[0].color;
}

/** Teal walkability class colors (5 EPA buckets). */
export function walkabilityClassColors(): Record<string, string> {
  const palette = getChoroplethPalette('benefit', 5);
  return {
    'Least walkable (0–20)': palette[0],
    'Below average (20–40)': palette[1],
    'Moderate (40–60)': palette[2],
    'Above average (60–80)': palette[3],
    'Highly walkable (80–100)': palette[4],
    Unknown: CHOROPLETH_NO_DATA_FILL,
  };
}

export function uhiPercentileColors(): string[] {
  return getChoroplethPalette('burden', 5);
}
